import { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

type ColorProps = {
  backgroundColor: string;
  borderColor: string;
  fontColor: string;
};

type ItemProps = {
  themeColors: ColorProps;
};

type ListItemProps = {
  children: React.ReactNode;
  isNew?: boolean;
  onClick?: () => void | null;
  onDragStart?: (event: React.DragEvent<HTMLElement>) => void;
  theme?: 'green' | 'grey' | 'lightBlue' | 'lightOrange' | 'red' | 'yellow';
  type?: 'default' | 'draggable' | 'addNew' | 'selectable' | 'selected';
  newItemRef?: any;
};

const ListItem = ({
  children,
  theme = 'grey',
  type = 'default',
  isNew = false,
  onClick,
  newItemRef,
  onDragStart,
}: ListItemProps) => {
  const itemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (isNew && itemRef.current) {
      itemRef.current.scrollIntoView();
    }
  }, [itemRef, isNew]);

  const colors: {
    [color: string]: ColorProps;
  } = {
    green: {
      fontColor: '#3c763d',
      backgroundColor: '#dff0d8',
      borderColor: '#3c763d',
    },
    grey: {
      fontColor: '#24292f',
      backgroundColor: '#fafafa',
      borderColor: '#e6e6e6',
    },
    lightBlue: {
      fontColor: '#24292f',
      backgroundColor: '#dce1f9',
      borderColor: '#627ae4',
    },
    lightOrange: {
      fontColor: '#24292f',
      backgroundColor: '#fee6d7',
      borderColor: '#fda772',
    },
    red: {
      fontColor: '#721c24',
      backgroundColor: '#f8d7da',
      borderColor: '#721c24',
    },
    yellow: {
      fontColor: '#856404',
      backgroundColor: '#fff3cd',
      borderColor: '#856404',
    },
  };

  const handleClick = () => {
    onClick && onClick();
  };

  const handleDragStart = (event: React.DragEvent<HTMLElement>) => {
    onDragStart && onDragStart(event);
  };

  const items: { [type: string]: JSX.Element } = {
    addNew: (
      <NewItem ref={newItemRef} themeColors={colors[theme]}>
        {children}
      </NewItem>
    ),
    default: <Item themeColors={colors[theme]}>{children}</Item>,
    draggable: (
      <DraggableItem
        draggable
        themeColors={colors[theme]}
        onDragStart={handleDragStart}
      >
        {children}
      </DraggableItem>
    ),
    selectable: (
      <SelectableItem themeColors={colors[theme]} onClick={handleClick}>
        {children}
      </SelectableItem>
    ),
    selected: (
      <Item ref={itemRef} themeColors={colors.lightOrange}>
        {children}
      </Item>
    ),
  };

  return items[type];
};
export default ListItem;

const appendAnimation = keyframes`
  0% {
    max-height: 0;
    transform: scaleX(0);
  }
  50% {
    max-height: 4rem;
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
`;

const Item = styled.li<ItemProps>`
  font-size: 0.8rem;
  color: ${({ themeColors }) => themeColors.fontColor};
  background-color: ${({ themeColors }) => themeColors.backgroundColor};
  border-radius: 0.25rem;
  border-bottom: 0.125rem solid ${({ themeColors }) => themeColors.borderColor};
  padding: 0.5rem;

  @media (min-width: 120rem) {
    font-size: 1rem;
  }
`;

const NewItem = styled(Item)`
  transform-origin: left;
  animation: ${appendAnimation} 0.6s linear;
`;

const SelectableItem = styled(Item)`
  cursor: pointer;

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.08) 0 1px 2px 1px;
  }
`;

const DraggableItem = styled(Item)`
  cursor: move;

  &:hover {
    box-shadow: rgba(0, 0, 0, 0.08) 0 1px 2px 1px;
  }
`;
